import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { BookOpen, AlertCircle, Shield, Users, Gamepad2, Scale } from 'lucide-react';
import axios from 'axios';
import Loader from '../components/Loader';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../components/ui/accordion';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const categoryIcons = {
  general: Scale,
  roleplay: Gamepad2,
  comportement: Users,
  sanctions: AlertCircle
};

const categoryLabels = {
  general: 'Règles Générales',
  roleplay: 'Règles Roleplay',
  comportement: 'Comportement',
  sanctions: 'Sanctions'
};

const Rules = () => {
  const [rules, setRules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState('all');

  useEffect(() => {
    const fetchRules = async () => {
      try {
        const response = await axios.get(`${API}/rules`);
        setRules(response.data);
      } catch (error) {
        console.error('Failed to fetch rules:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchRules();
  }, []);

  // Group rules by category
  const groupedRules = rules.reduce((acc, rule) => {
    const category = rule.category || 'general';
    if (!acc[category]) {
      acc[category] = [];
    }
    acc[category].push(rule);
    return acc;
  }, {});

  const categories = Object.keys(groupedRules);

  const filteredRules = selectedCategory === 'all' 
    ? rules 
    : rules.filter(r => r.category === selectedCategory);

  if (loading) {
    return <Loader fullScreen />;
  }

  return (
    <div className="min-h-screen pt-24 pb-16">
      {/* Header */}
      <section className="py-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-xl bg-violet-600/20 mb-6">
              <BookOpen className="w-8 h-8 text-violet-400" />
            </div>
            
            <h1 className="font-heading font-bold text-4xl sm:text-5xl uppercase tracking-wide mb-4">
              <span className="gradient-text">Règlement</span>
            </h1>
            <p className="text-gray-400 text-lg max-w-2xl mx-auto">
              Pour garantir une expérience roleplay de qualité, nous vous demandons de respecter les règles suivantes.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Category Filter */}
      {categories.length > 0 && (
        <section className="px-4 sm:px-6 lg:px-8 mb-8">
          <div className="max-w-4xl mx-auto">
            <div className="flex flex-wrap gap-2 justify-center">
              <button
                onClick={() => setSelectedCategory('all')}
                data-testid="filter-all"
                className={`px-4 py-2 rounded-lg font-heading uppercase tracking-wider text-sm transition-all ${
                  selectedCategory === 'all'
                    ? 'bg-violet-600 text-white'
                    : 'bg-white/5 text-gray-400 hover:text-white hover:bg-white/10'
                }`}
              >
                Tout
              </button>
              {categories.map((category) => {
                const Icon = categoryIcons[category] || Shield;
                return (
                  <button
                    key={category}
                    onClick={() => setSelectedCategory(category)}
                    data-testid={`filter-${category}`}
                    className={`flex items-center gap-2 px-4 py-2 rounded-lg font-heading uppercase tracking-wider text-sm transition-all ${
                      selectedCategory === category
                        ? 'bg-violet-600 text-white'
                        : 'bg-white/5 text-gray-400 hover:text-white hover:bg-white/10'
                    }`}
                  >
                    <Icon className="w-4 h-4" />
                    {categoryLabels[category] || category}
                  </button>
                );
              })}
            </div>
          </div>
        </section>
      )}

      {/* Rules List */}
      <section className="px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto">
          {rules.length === 0 ? (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="glass rounded-xl p-12 text-center"
            >
              <Shield className="w-16 h-16 text-violet-500/50 mx-auto mb-4" />
              <p className="text-gray-400 text-lg">
                Le règlement sera bientôt disponible.
              </p>
            </motion.div>
          ) : (
            <Accordion type="single" collapsible className="space-y-3">
              {filteredRules.map((rule, index) => (
                <AccordionItem 
                  key={rule.id} 
                  value={rule.id}
                  className="glass rounded-xl border-0 overflow-hidden"
                >
                  <AccordionTrigger 
                    className="px-6 py-4 hover:no-underline hover:bg-white/5 transition-colors"
                    data-testid={`rule-${index}`}
                  >
                    <div className="flex items-center gap-4 text-left">
                      <span className="flex items-center justify-center w-8 h-8 rounded-lg bg-violet-600/20 text-violet-400 font-heading font-bold text-sm">
                        {index + 1}
                      </span>
                      <span className="font-heading font-semibold text-white uppercase tracking-wider">
                        {rule.title}
                      </span>
                    </div>
                  </AccordionTrigger>
                  <AccordionContent className="px-6 pb-6 pt-2">
                    <div className="pl-12">
                      <p className="text-gray-300 leading-relaxed whitespace-pre-wrap">
                        {rule.content}
                      </p>
                      {rule.category && (
                        <span className="inline-flex items-center gap-1 mt-4 px-3 py-1 rounded-full bg-white/5 text-xs text-gray-400 uppercase tracking-wider">
                          {(() => {
                            const Icon = categoryIcons[rule.category] || Shield;
                            return <Icon className="w-3 h-3" />;
                          })()}
                          {categoryLabels[rule.category] || rule.category}
                        </span>
                      )}
                    </div>
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          )}
        </div>
      </section>

      {/* Important Notice */}
      <section className="px-4 sm:px-6 lg:px-8 mt-12">
        <div className="max-w-4xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="glass rounded-xl p-6 border border-yellow-500/20 bg-yellow-500/5"
          >
            <div className="flex items-center justify-center gap-4">
              <div className="text-center">
                <AlertCircle className="w-6 h-6 text-yellow-400 flex-shrink-0" />
                <h3 className="font-heading font-bold text-yellow-400 uppercase tracking-wider mb-2">
                  Important
                </h3>
                <p className="text-gray-300 text-sm leading-relaxed">
                  Le non-respect du règlement peut entraîner des sanctions allant de l'avertissement au bannissement permanent. 
                  L'équipe de modération se réserve le droit de modifier ces règles à tout moment.
                </p>
              </div>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default Rules;
